/**
 * School License Management - Tab 2 of School Admin Dashboard
 *
 * Displays:
 * - License details (tier, seats, contract info)
 * - Seat allocation by grade level
 * - Seat allocation by department/subject
 * - Billing information
 * - Contract details
 *
 * Data Source: GET /api/admin/school/license (session-based - no ID)
 */

import React, { useState, useEffect } from 'react';
import axios from '../utils/authInterceptor';

// Tappy Brand Colors - Official 2025 Design System
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkGray: '#3D4954',
    mediumGray: '#8D7F88',
    lightTeal: '#ABCACA',
    paleGray: '#D5DEDD',
    lightGray: '#ECF3F2',
    veryLight: '#F5F9FA',
    almostWhite: '#FCFDFE',
  },
  semantic: {
    success: '#42909E',
    warning: '#ECC962',
    error: '#EA6D37',
    info: '#6DB4CD',
  }
};

function SchoolLicenseManagement({ getApiUrl, isDistrictAdminView }) {
  const [loading, setLoading] = useState(true);
  const [licenseData, setLicenseData] = useState(null);
  const [error, setError] = useState(null);

  // Helper to get API URL - use prop if provided, otherwise default to session-based
  const buildApiUrl = (endpoint) => {
    if (getApiUrl) {
      return getApiUrl(endpoint);
    }
    return `/api/admin/school/${endpoint}`;
  };

  useEffect(() => {
    fetchLicenseData();
  }, []);

  const fetchLicenseData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl('license'));
      setLicenseData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching license data:', err);
      setError(err.response?.data?.error || 'Failed to load license data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <p style={{ color: '#6b7280' }}>Loading license details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          backgroundColor: TappyBrandColors.accent.coral + '15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          color: TappyBrandColors.accent.coral
        }}>!</div>
        <h3 style={{ color: TappyBrandColors.accent.coral, marginBottom: '12px', fontWeight: 600 }}>Error Loading License</h3>
        <p style={{ color: TappyBrandColors.neutral.mediumGray, marginBottom: '20px' }}>{error}</p>
        <button
          onClick={fetchLicenseData}
          style={{
            padding: '10px 20px',
            backgroundColor: TappyBrandColors.primary.teal,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!licenseData?.license) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 20px',
          borderRadius: '12px',
          backgroundColor: TappyBrandColors.neutral.lightGray,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          color: TappyBrandColors.neutral.mediumGray
        }}>□</div>
        <p style={{ color: TappyBrandColors.neutral.mediumGray }}>No license information available</p>
      </div>
    );
  }

  const { license, allocation } = licenseData;

  // Calculate expiration alert
  const daysUntilExpiration = license.expiration_date
    ? Math.floor((new Date(license.expiration_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration < 60;

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
          License Management
        </h2>
        <p style={{ fontSize: '14px', color: '#6b7280' }}>
          View contract details, seat allocation, and billing information
        </p>
      </div>

      {/* Expiration Alert */}
      {isExpiringSoon && (
        <div style={{
          marginBottom: '24px',
          padding: '16px 20px',
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: TappyBrandColors.semantic.warning,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            color: 'white',
            flexShrink: 0
          }}>!</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: TappyBrandColors.accent.gold, marginBottom: '4px' }}>
              License Expiring in {daysUntilExpiration} Days
            </div>
            <div style={{ fontSize: '14px', color: '#78350f' }}>
              Your license expires on {new Date(license.expiration_date).toLocaleDateString()}.
              Contact care@teachtappy.com to renew.
            </div>
          </div>
          <button style={{
            padding: '8px 16px',
            backgroundColor: '#fbbf24',
            color: '#78350f',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}>
            Renew Now
          </button>
        </div>
      )}

      {/* License Overview Card */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '24px' }}>
          License Details
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
          {/* School Name */}
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
              School Name
            </div>
            <div style={{ fontSize: '16px', color: '#1f2937', fontWeight: 500 }}>
              {license.school_name}
            </div>
          </div>

          {/* License Tier */}
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
              License Tier
            </div>
            <div style={{ fontSize: '16px', color: '#1f2937', fontWeight: 500 }}>
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                borderRadius: '12px',
                fontSize: '14px'
              }}>
                {license.tier.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Status */}
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
              Status
            </div>
            <div style={{ fontSize: '16px', color: '#1f2937', fontWeight: 500 }}>
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: license.status === 'active' ? TappyBrandColors.semantic.success + '15' : TappyBrandColors.semantic.error + '15',
                color: license.status === 'active' ? TappyBrandColors.semantic.success : TappyBrandColors.semantic.error,
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                {license.status === 'active' ? 'Active' : license.status}
              </span>
            </div>
          </div>

          {/* Total Seats */}
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
              Total Seats
            </div>
            <div style={{ fontSize: '24px', color: '#1f2937', fontWeight: 700 }}>
              {license.seat_count.toLocaleString()}
            </div>
          </div>

          {/* Seats Used */}
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
              Seats Used
            </div>
            <div style={{ fontSize: '24px', color: '#10b981', fontWeight: 700 }}>
              {license.seats_used.toLocaleString()}
            </div>
            <div style={{
              marginTop: '8px',
              height: '4px',
              backgroundColor: '#e5e7eb',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${Math.min((license.seats_used / license.seat_count) * 100, 100)}%`,
                backgroundColor: license.seats_used / license.seat_count > 0.9 ? '#ef4444' : '#10b981',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* Seats Available */}
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
              Seats Available
            </div>
            <div style={{ fontSize: '24px', color: '#6b7280', fontWeight: 700 }}>
              {license.seats_available.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Dates */}
        <div style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e5e7eb',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          {license.purchase_date && (
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Purchase Date</div>
              <div style={{ fontSize: '14px', color: '#1f2937' }}>
                {new Date(license.purchase_date).toLocaleDateString()}
              </div>
            </div>
          )}
          {license.activation_date && (
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Activation Date</div>
              <div style={{ fontSize: '14px', color: '#1f2937' }}>
                {new Date(license.activation_date).toLocaleDateString()}
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Expiration Date</div>
            <div style={{ fontSize: '14px', color: isExpiringSoon ? '#dc2626' : '#1f2937', fontWeight: isExpiringSoon ? 600 : 400 }}>
              {new Date(license.expiration_date).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      {/* Seat Allocation by Grade */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>
          Seat Allocation by Grade Level
        </h3>

        {allocation?.by_grade && allocation.by_grade.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Grade Level
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Total Students
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Active (30 days)
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Utilization
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>

                  </th>
                </tr>
              </thead>
              <tbody>
                {allocation.by_grade.map((row, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '16px 12px', fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                      {row.grade_level || 'Unknown'}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', color: '#1f2937' }}>
                      {row.student_count}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', color: '#10b981', fontWeight: 500 }}>
                      {row.active_count}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', color: '#1f2937', fontWeight: 600 }}>
                      {row.utilization_pct}%
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{
                        height: '6px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        width: '100px'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${row.utilization_pct}%`,
                          backgroundColor: row.utilization_pct >= 80 ? '#10b981' : row.utilization_pct >= 50 ? '#f59e0b' : '#ef4444',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
            No grade-level data available
          </p>
        )}
      </div>

      {/* Seat Allocation by Department */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>
          Seat Allocation by Department
        </h3>

        {allocation?.by_department && allocation.by_department.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Subject/Department
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Teachers
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Active Teachers
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Students Enrolled
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Adoption
                  </th>
                </tr>
              </thead>
              <tbody>
                {allocation.by_department.map((row, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '16px 12px', fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                      {row.subject || 'Other'}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', color: '#1f2937' }}>
                      {row.teachers_total}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', color: '#10b981', fontWeight: 500 }}>
                      {row.teachers_active}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', color: '#1f2937' }}>
                      {row.students_enrolled}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', color: '#1f2937', fontWeight: 600 }}>
                      {row.adoption_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
            No department data available
          </p>
        )}
      </div>

      {/* Billing & Contract Information */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '24px' }}>
          Billing & Contract Information
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
          {/* Annual Cost */}
          {license.annual_cost && (
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
                Annual Cost
              </div>
              <div style={{ fontSize: '24px', color: '#1f2937', fontWeight: 700 }}>
                ${license.annual_cost.toLocaleString()}
              </div>
            </div>
          )}

          {/* Contract Number */}
          {license.contract_number && (
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
                Contract Number
              </div>
              <div style={{ fontSize: '16px', color: '#1f2937', fontFamily: 'monospace' }}>
                {license.contract_number}
              </div>
            </div>
          )}

          {/* District ID */}
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 500 }}>
              District ID
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'monospace' }}>
              {license.district_id}
            </div>
          </div>
        </div>

        {/* Billing Contact */}
        {license.billing_contact && (license.billing_contact.name || license.billing_contact.email) && (
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', marginBottom: '12px' }}>
              Billing Contact
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {license.billing_contact.name && (
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Name</div>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>{license.billing_contact.name}</div>
                </div>
              )}
              {license.billing_contact.email && (
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Email</div>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>{license.billing_contact.email}</div>
                </div>
              )}
              {license.billing_contact.phone && (
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Phone</div>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>{license.billing_contact.phone}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Support */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Questions about your license? Contact us at{' '}
            <a href="mailto:care@teachtappy.com" style={{ color: '#4F46E5', textDecoration: 'none', fontWeight: 500 }}>
              care@teachtappy.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SchoolLicenseManagement;
